import type { FeatureName } from '@/config/features.config';
import { ROUTES } from '@/config/routes.config';

type NavLinkItem = {
  url: string;
  label: string;
  disabled?: boolean;
  feature?: FeatureName;
  /** Rutas que marcan este ítem como activo (para items que agrupan varias vistas, ej. Diseño + Tarifas) */
  activePaths?: string[];
  /** Presente = fuera del menú para todos. Ver `PaginaRetirada`, más abajo. */
  retirada?: PaginaRetirada;
};

/**
 * Sub-items del Sidebar principal (Admin).
 * Solo data: el Sidebar agrega `badge`, `matches`, y `onClick`.
 */
export const SIDEBAR_SUBNAV = {
  /* Hijos de Inventario de Drop (plan de actores): cada uno es su propia lista estándar. */
  inventarioDrop: [
    { url: ROUTES.DROPSHIPPING_SMARTYDROP_INGRESOS, label: 'Ingresos' },
    { url: ROUTES.DROPSHIPPING_SMARTYDROP_ENCARGOS, label: 'Encargos' },
  ],
  home: [
    { url: ROUTES.HOME, label: 'Resumen' },
    /* El checklist de onboarding: lee el estado real y lleva a donde se hace cada cosa. */
    { url: ROUTES.PRIMEROS_PASOS, label: 'Primeros pasos' },
    /*
     * 🚨 RETIRADOS, NO GATEADOS. El founder: «no están listos». Eso no es «este plan no lo
     *    contrató» — es que la pantalla todavía no sirve para NADIE, y por eso usa `retirada`, que
     *    es el mecanismo para exactamente esto. Además son DOS vistas del mismo tablero: la
     *    duplicación es parte de lo que falta resolver.
     */
    {
      url: ROUTES.CONTROL_CENTER, label: 'Dashboard',
      retirada: { motivo: 'Pendiente por construir: el tablero no está terminado, y convive con «Dashboard 2» sin que esté decidido cuál queda.', desde: '2026-08-25' },
    },
    {
      url: ROUTES.CONTROL_CENTER_2, label: 'Dashboard 2',
      retirada: { motivo: 'Pendiente por construir: es la segunda versión del mismo tablero; falta decidir cuál reemplaza a cuál.', desde: '2026-08-25' },
    },
  ] satisfies NavLinkItem[],
  /**
   * Pedidos: lo que entra por la web, antes de ser una venta gestionada.
   *
   * 🪤 ESTE L1 SE RETIRÓ Y VOLVIÓ, por decisión del founder. El argumento para sacarlo era bueno
   *    —su objeto es `orders_v2`, el MISMO que el de Ventas, así que son dos entradas de primer
   *    nivel para una sola cosa— pero fusionarlo dejaba una pantalla de Ventas con ocho ítems y
   *    dos formas distintas de mirar lo mismo, y todavía no cierra. Se deja separado a propósito
   *    hasta decidir bien cómo conviven. Ver ADR 0029 §5.
   */
  orders: [
    { url: ROUTES.ORDERS_ONLINE, label: 'Pedidos online' },
    { url: ROUTES.ORDERS_ABANDONED, label: 'Carritos abandonados' },
    /* El Confirmador y el despacho automático: qué hizo (o habría hecho) con cada pedido, y por qué. */
    { url: ROUTES.ORDERS_CONFIRMACIONES, label: 'Confirmaciones' },
    { url: ROUTES.ORDERS_DESPACHO, label: 'Despacho automático' },
    { url: ROUTES.ORDERS_PANEL, label: 'Panel de operación' },
  ] satisfies NavLinkItem[],
  dropshipping: [
    /*
     * 🪤 DROPI Y ALICLIK EXIGEN `proveedores`; SMARTY DROP NO.
     *
     * Los tres colgaban de `logistics`, que es la única feature del plan Drop — o sea que un
     * revendedor gratuito veía los catálogos de la competencia. Le estaríamos pagando el
     * servidor a Aliclik para que ellos cobren la venta, y encima poniendo su catálogo al lado
     * del nuestro dentro de nuestro propio producto.
     *
     * Al revés es el mejor argumento de venta que tenemos: «ya vendés con Aliclik y con Dropi —
     * juntá los tres en un solo panel». Eso se cobra, no se regala.
     */
    // ⭐ EL ORDEN (21-sep-2026): las PLATAFORMAS primero, por su marca —la nuestra adelante—; después
    //    la plata (Billetera, Cobros); al final el mostrador (Mi almacén). Ver `MENU_DEL_ROL_DROP`.
    // UNA entrada para la plataforma, no una por proveedor: con cincuenta proveedores el menú
    // sería impracticable. Los proveedores viven adentro, como filtro.
    { url: ROUTES.DROPSHIPPING_SMARTYDROP, label: 'Smarty Drop' },
    { url: ROUTES.DROPSHIPPING_DROPI, label: 'Dropi', feature: 'proveedores' as FeatureName },
    { url: ROUTES.DROPSHIPPING_ALICLIK, label: 'Aliclik', feature: 'proveedores' as FeatureName },
    /*
     * 🪤 ESTABAN ESCONDIDOS. «Mis pedidos» sólo se alcanzaba por un botón secundario del catálogo,
     * y el saldo vivía enterrado DENTRO de esa pantalla. El founder miró el panel y preguntó
     * «¿dónde se ve la billetera?» — que es la prueba de que no se veía.
     *
     * Aliclik tiene «Billetera» como entrada de primer nivel. Un número que el revendedor mira
     * varias veces por día no puede estar a dos clics de profundidad.
     */
    /*
     * 🪤 «MIS COMPRAS», NO «MIS PEDIDOS». Al lado de «Pedidos» en el menú principal, «Mis
     *    pedidos» leía como lo mismo con otro nombre, y el founder lo preguntó: «no sé si esto
     *    sea redundante ya que hay una página de ventas».
     *
     *    No es redundante, es lo contrario: Ventas son sus ventas a SUS clientes; esto son sus
     *    COMPRAS al almacén. La plata va en direcciones opuestas. El nombre tenía que decirlo.
     */
    { url: ROUTES.DROPSHIPPING_SMARTYDROP_PEDIDOS, label: 'Mis compras' },
    /*
     * 🪤 ACÁ VIVÍAN TAMBIÉN «Billetera», «Cobros» y «Mi almacén», Y SE FUERON ARRIBA (22-sep-2026).
     *
     *    El founder: «billetera también sacarlo como una opción padre y no como hija… porque dentro
     *    de Drop confundiré al dropshipper». Tenía razón por dos motivos distintos:
     *
     *    · La billetera es un número que se mira varias veces por día. Vivir a dos clics, dentro de
     *      un módulo que se llama como OTRA cosa (el catálogo), es esconderla.
     *    · «Cobros» ni siquiera era del mismo dueño: es la caja de Smarty, no del revendedor.
     *
     *    Ahora son ítems de primer nivel dentro del grupo «Dropshipping». Acá queda sólo lo que es
     *    del CATÁLOGO: dónde comprar y qué compré.
     */
  ] satisfies NavLinkItem[],
  products: [
    // Categorías y Etiquetas se quedan para todos: organizan el catálogo propio Y el importado.
    { url: ROUTES.PRODUCTS_CATEGORIES, label: 'Categorías' },
    { url: ROUTES.PRODUCTS_TAGS, label: 'Etiquetas' },
    /*
     * 🪤 LAS CUATRO DE ABAJO PRESUPONEN QUE EL PRODUCTO ES TUYO. El founder lo dijo exacto: «el
     *    dropshipper jala el producto tal cual de Smarty Drop». No define variantes, no pone
     *    ofertas del storefront, no arma modificadores de restaurante — y sobre todo NO ajusta
     *    existencias: el stock es del almacén.
     *
     * 🚨 «Inventario» es el que de verdad importaba sacar. Dejar que ajuste el stock de productos
     *    que no son suyos no es ruido en el menú: es una pantalla que puede romper el dato del que
     *    depende que un pedido se pueda despachar.
     */
    { url: ROUTES.PRODUCTS_MODIFIERS, label: 'Modificadores', feature: 'catalogo_propio' as FeatureName },
    { url: ROUTES.PRODUCTS_VARIANTS, label: 'Variantes', feature: 'catalogo_propio' as FeatureName },
    { url: ROUTES.PRODUCTS_OFFERS, label: 'Ofertas', feature: 'catalogo_propio' as FeatureName },
    { url: ROUTES.PRODUCTS_INVENTORY, label: 'Inventario', feature: 'catalogo_propio' as FeatureName },
    {
      url: ROUTES.PRODUCTS_PHOTO_STUDIO,
      label: 'Estudio de foto',
      retirada: {
        motivo: 'Pendiente por construir: falta desplegar la función y cargar la clave del proveedor de IA, así que hoy la pantalla abre pero no genera ninguna foto.',
        desde: '2026-08-21',
      },
    },
    /*
     * 🚨 «PREPARACIÓN MARKETPLACES» YA NO SE OFRECE DESDE ACÁ — SE ENTRA POR MARKETPLACES.
     *
     * Pregunta del founder: «¿no debería estar en el módulo canal marketplaces?». Sí, y había una
     * evidencia dura: la misma función ya se mostraba en DOS lugares —una tarjeta resumen dentro de
     * Marketplaces y esta pantalla dentro de Productos— y la que estaba acá se rompió al abrirse
     * sin que nadie lo notara durante semanas. Una pantalla que nadie visita es una pantalla
     * ubicada donde no corresponde.
     *
     * ⭐ Y LA RAZÓN DE FONDO: LAS REGLAS SON DEL CANAL, NO DEL CATÁLOGO. El catálogo es uno solo y
     * se comparte; lo que cada marketplace EXIGE es propio de cada uno. El día que Falabella pida
     * cosas distintas de Mercado Libre, una única «preparación» colgada de Productos tiene que
     * mentir o mezclar dos verdades. Colgada del canal, es una vista por canal.
     *
     * Es lo que hacen Shopify (la salud de cada canal vive dentro del canal) y Amazon («Fix your
     * products» adentro del inventario de ese canal). La ruta sigue viva: se entra desde la
     * tarjeta de Marketplaces.
     */
  ] satisfies NavLinkItem[],
  /**
   * ⭐ MARKETPLACES GANA SUBMENÚ. Pedido del founder: «ubicalo como subopción en marketplaces».
   *
   * Y es lo que faltaba para cerrar el movimiento: la pantalla ya se entraba desde la tarjeta del
   * canal, pero sin un renglón en el menú seguía sin existir para quien la busca en vez de
   * tropezársela. Una pantalla a la que sólo se llega por un enlace adentro de otra pantalla se
   * encuentra una vez y no se vuelve a encontrar.
   */
  marketplaces: [
    { url: ROUTES.MARKETPLACES, label: 'Canales conectados' },
    { url: ROUTES.MARKETPLACES_READINESS, label: 'Preparación del catálogo' },
  ] satisfies NavLinkItem[],
  reports: [
    { url: ROUTES.REPORTES, label: 'Reportes' },
    { url: ROUTES.SALES_HEATMAP, label: 'Mapa de calor' },
    { url: ROUTES.SALES_RANKING, label: 'Ranking de productos' },
    { url: ROUTES.SIMULADOR_RENTABILIDAD, label: 'Simulador de rentabilidad' },
    { url: ROUTES.FINANZAS_PNL, label: 'Finanzas (P&L)' },
    { url: ROUTES.COMISIONES, label: 'Comisiones de asesores' },
  ] satisfies NavLinkItem[],
  onlineStore: [
    { url: ROUTES.ONLINE_STORE_THEMES, label: 'Temas' },
    { url: ROUTES.ONLINE_STORE_PAGES, label: 'Páginas' },
    { url: ROUTES.ONLINE_STORE_NAVIGATION, label: 'Menús (Navegación)' },
    { url: ROUTES.ONLINE_STORE_PREFERENCES, label: 'Preferencias' },
  ] satisfies NavLinkItem[],
  pos: [
    { url: ROUTES.APPS_RESTAURANT_POS, label: 'POS', feature: 'pos' as FeatureName },
    { url: ROUTES.APPS_RESTAURANT_CUENTAS, label: 'Cuentas', feature: 'pos' as FeatureName },
    { url: ROUTES.APPS_RESTAURANT_TABLES, label: 'Mesas', feature: 'pos' as FeatureName },
    { url: ROUTES.APPS_RESTAURANT_RESERVAS, label: 'Reservas', feature: 'pos' as FeatureName },
    { url: ROUTES.APPS_RESTAURANT_KDS, label: 'KDS', feature: 'kds' as FeatureName },
    { url: ROUTES.APPS_RESTAURANT_MENU, label: 'Menú', feature: 'pos' as FeatureName },
  ] satisfies NavLinkItem[],
  loyalty: [
    // S3 · Sellos: el programa de tarjetas de sellos (reemplaza a los puntos por sol, que nunca tuvieron un cliente).
    { url: ROUTES.APPS_LOYALTY_QR_SCANNER, label: 'Sellar', feature: 'loyalty' as FeatureName },
    { url: ROUTES.APPS_LOYALTY_DASHBOARD, label: 'Panel' },
    { url: ROUTES.APPS_LOYALTY_CUSTOMERS, label: 'Tarjetas' },
    { url: ROUTES.APPS_LOYALTY_CASHIERS, label: 'Cajeros' },
    { url: ROUTES.APPS_LOYALTY_GIFTS, label: 'Regalos' },
    { url: ROUTES.APPS_LOYALTY_POSTER, label: 'Cartel' },
    { url: ROUTES.APPS_LOYALTY_SETTINGS, label: 'Ajustes' },
  ] satisfies NavLinkItem[],
  contabilidad: [
    { url: ROUTES.APPS_CONTABILIDAD, label: 'Resumen' },
    { url: ROUTES.APPS_CONTABILIDAD_COMPROBANTES, label: 'Comprobantes' },
    { url: ROUTES.APPS_CONTABILIDAD_CONFIG, label: 'Configuración' },
  ] satisfies NavLinkItem[],
  /** Formulario COD: solo configuración del formulario de compra (diseño, integraciones, etc.) */
  cod: [
    { url: ROUTES.APPS_COD_DESIGN, label: 'Diseño del Formulario', activePaths: [ROUTES.APPS_COD_DESIGN, ROUTES.APPS_COD_SHIPPING] },
    { url: ROUTES.APPS_COD_FRAUD, label: 'Prevención de Fraude' },
    { url: ROUTES.APPS_COD_DELIVERY_SUCCESS, label: 'Éxito de Entrega' },
    { url: ROUTES.APPS_COD_SALES_BOOSTER, label: 'Impulsor de Ventas' },
    { url: ROUTES.APPS_COD_ANALYTICS, label: 'Analítica' },
    { url: ROUTES.APPS_COD_PAYMENTS, label: 'Adelanto' },
    { url: ROUTES.APPS_COD_SETTINGS, label: 'Configuraciones e integraciones' },
  ] satisfies NavLinkItem[],
  /** WhatsApp: inbox y configuración */
  whatsapp: [
    { url: ROUTES.WHATSAPP_INBOX, label: 'Inbox' },
    // Campañas se mudó a Automatización → Difusiones (12-sep-2026): Chats es la bandeja.
    /*
     * 🚨 LAS PLANTILLAS NO SON UN AJUSTE: TIENEN UN ESTADO QUE CAMBIA SIN QUE NADIE LAS TOQUE.
     * Un ajuste se pone y se queda. Una plantilla la aprueba Meta, la rechaza, o la recategoriza
     * —de madrugada y sin avisar—, y una rechazada significa que un mensaje NO va a salir. Eso es
     * algo que se MIRA, no algo que se configura, y lo que se mira vive donde se trabaja.
     *
     * Es la misma prueba que separa «Pedidos» de «Ajustes de pedidos»: si tiene ciclo de vida
     * propio, sube. Twilio, 360dialog y Wati las ponen de primer nivel por lo mismo; Intercom y
     * Zendesk guardan sus respuestas en ajustes porque a ésas no las aprueba nadie externo.
     *
     * 🪤 Y ESTABAN ENTERRADAS EN LA CONFIGURACIÓN DEL CANAL, que es exactamente el error que este
     * menú ya cometió con Campañas —ver la nota de arriba—.
     */
    // Plantillas vive en Automatización → Contenido desde el 12-sep-2026.
    { url: ROUTES.WHATSAPP_SUPERVISOR, label: 'Supervisor' },
    // F4: la cola de escalaciones del agente — pendientes, SLA y «Marcar atendida».
    { url: ROUTES.WHATSAPP_ESCALACIONES, label: 'Escalaciones' },
    { url: ROUTES.WHATSAPP_SLA, label: 'SLA y alertas' },
    { url: ROUTES.WHATSAPP_STATS, label: 'Métricas' },
    // F9: la conversión por cohorte — «de 100 que entraron, cuántas llegaron a cada etapa».
    { url: ROUTES.WHATSAPP_EMBUDO, label: 'Embudo' },
    { url: ROUTES.WHATSAPP_TEAMS, label: 'Equipos' },
    { url: ROUTES.WHATSAPP_ASSIGNMENT_RULES, label: 'Reglas de asignación' },
    { url: ROUTES.WHATSAPP_SKILLS, label: 'Skills y enrutamiento' },
    { url: ROUTES.WHATSAPP_MACROS, label: 'Macros' },
    { url: ROUTES.WHATSAPP_AUTOMATED_MESSAGES, label: 'Mensajes automáticos' },
    { url: ROUTES.WHATSAPP_WEBHOOKS, label: 'Webhooks' },
    // 'Configuración' (WHATSAPP_SETTINGS) removido — la config ahora vive
    // por canal en /chats/inboxes/{id}/{section}. La ruta legacy redirige.
  ] satisfies NavLinkItem[],
  /** CRM: contactos, deals, comunicaciones (las opciones de voz/centralita viven en `callCenter`). */
  /*
   * Clientes gana submenú: la lista, y la pregunta que la lista no puede responder sola.
   *
   * 🪤 «Por recomprar» NO es un filtro de la lista, y por eso es una entrada aparte. La lista mira
   *    al cliente (cuánto gastó, cuándo compró); esto mira el PRODUCTO que se le acabó — con su
   *    duración— y ordena por cuánto hace que debería haber vuelto. Meterlo como filtro obligaría a
   *    la lista a cargar una dimensión que no tiene.
   */
  customers: [
    { url: ROUTES.CUSTOMERS, label: 'Clientes' },
    { url: ROUTES.CUSTOMERS_RECOMPRA, label: 'Por recomprar' },
  ] satisfies NavLinkItem[],
  crm: [
    { url: ROUTES.APPS_CRM_INBOX, label: 'Inbox' },
    { url: ROUTES.APPS_CRM_LEADS, label: 'Contactos' },
    { url: ROUTES.APPS_CRM_PIPELINE, label: 'Pipeline' },
    { url: ROUTES.APPS_CRM_TASKS, label: 'Tareas' },
    { url: ROUTES.APPS_CRM_SCHEDULE, label: 'Agenda' },
    { url: ROUTES.APPS_CRM_SEQUENCES, label: 'Secuencias' },
    { url: ROUTES.APPS_CRM_DASHBOARD, label: 'Dashboard' },
    { url: ROUTES.APPS_CRM_AUTOMATIONS, label: 'Automatizaciones' },
    { url: ROUTES.APPS_CRM_EMAIL, label: 'Email' },
    { url: ROUTES.APPS_CRM_EMAIL_TEMPLATES, label: 'Plantillas de email' },
    { url: ROUTES.APPS_CRM_RECOMPRA, label: 'Recompra' },
    { url: ROUTES.APPS_CRM_COPILOT, label: 'Copiloto', feature: 'copilot' as FeatureName },
    { url: ROUTES.APPS_CRM_CONVERSION, label: 'Reporte de conversión' },
    { url: ROUTES.APPS_CRM_MERGE, label: 'Fusionar contactos' },
  ] satisfies NavLinkItem[],
  /** Llamadas: módulo principal de voz (Voice Commerce). Item L1 dedicado en sidebar. */
  callCenter: [
    { url: ROUTES.APPS_CRM_DIALER, label: 'Marcador' },
    { url: ROUTES.APPS_CRM_CALLS, label: 'Historial' },
    { url: ROUTES.APPS_CALL_CENTER_CART_RECOVERY, label: 'Cart Recovery' },
    { url: ROUTES.APPS_CALL_CENTER_CAMPAIGNS, label: 'Campañas' },
    { url: ROUTES.APPS_CALL_CENTER_SUPERVISOR, label: 'Supervisión' },
    { url: ROUTES.APPS_CALL_CENTER_STATS, label: 'Estadísticas' },
    { url: ROUTES.APPS_CALL_CENTER_EXTENSIONS, label: 'Extensiones' },
    { url: ROUTES.APPS_CRM_VOICE_AI, label: 'Voice AI' },
    { url: ROUTES.APPS_CRM_CALL_COACHING, label: 'Coaching de llamadas' },
    { url: ROUTES.APPS_CALL_CENTER, label: 'Configuración' },
  ] satisfies NavLinkItem[],
  /** Operaciones: pedidos, ventas, logística y cobranzas. (Transporte es su propio ítem.) */
  operaciones: [
    // Dominio único (orders_v2). Las páginas COD legacy (Ventas/Logística/Cobranzas) quedaron
    // consolidadas aquí — leen el mismo orders_v2 vía las vistas.
    // «Presupuestos» era vocabulario B2B heredado de Odoo: nadie que compra por Facebook pide un
    // presupuesto. El asesor dice «tengo 40 pedidos por confirmar», y eso es lo que se muestra.
    { url: ROUTES.OPS_PEDIDOS, label: 'Por confirmar' },
    { url: ROUTES.OPS_VENTAS, label: 'Ventas' },
    // 🪤 ACÁ ESTABA «Entregas», y era el mismo query que Ventas con otro filtro: `stages
    //    ['sale','fulfilled']` contra `['sale','fulfilled','closed']`. Un subconjunto propio, con
    //    las mismas columnas y las mismas acciones — por eso el founder preguntó para qué servía.
    //    Las entregas viven en su módulo (ADR 0029), donde el objeto es el ENVÍO y las columnas
    //    (guía, courier, días en la calle) no existen en esta lista. NO volver a agregarla acá.
    { url: ROUTES.OPS_COBRANZAS, label: 'Cobranzas' },
    { url: ROUTES.OPS_CANCELADAS, label: 'Canceladas' },
    /*
     * 🪤 SÓLO PARA QUIEN ES PROVEEDOR, igual que «Mi almacén». Un tenant que no publica catálogo
     *    mayorista nunca va a recibir un pedido por Smarty Drop, y una entrada permanente a una
     *    pantalla vacía es ruido en el menú de TODAS las tiendas.
     *
     * ⭐ Y ES UN FILTRO, no un desvío: estos pedidos siguen apareciendo en «Ventas» y en «Por
     *    confirmar» según su etapa. Se pidió así explícitamente.
     */
    { url: ROUTES.OPS_DROP, label: 'Drop' },
    // El §5 pide «Reportes de ventas» y apunta acá, no a Analíticas: este es el reporte DEL
    // módulo (embudo de confirmación, cobranza, canal). Y estaba huérfano — la pantalla existía,
    // con la tarjeta de F7 adentro, pero no había forma de llegar salvo escribiendo la URL.
    {
      url: ROUTES.OPS_DASHBOARD,
      label: 'Reportes de ventas',
      /*
       * 🪤 VA COMO `retirada` Y NO COMO `feature`, y la diferencia importa. `feature` dice «este
       *    plan no lo contrató»; acá el founder dijo «no está terminada», que es otra cosa: no
       *    tiene caso mostrárselo a NADIE, ni al que paga. Marcado así aparece solo en la lista de
       *    «Sin construir» del super admin, que es justamente para no olvidarse de retomarlo.
       */
      retirada: { motivo: 'Pendiente por construir: el reporte del módulo no está terminado.', desde: '2026-08-25' },
    },
    // Al final: configuración del módulo (couriers, roles, defaults). Solo admin (filtrado en Sidebar).
    {
      url: ROUTES.OPS_SETTINGS,
      label: 'Configuraciones',
      retirada: { motivo: 'Pendiente por construir: la configuración del módulo de ventas no está terminada.', desde: '2026-08-25' },
    },
  ] satisfies NavLinkItem[],
  /**
   * Entregas (ADR 0029) — módulo del objeto ENVÍO (`order_shipments`).
   *
   * 🪤 NO agregar acá vistas de PEDIDOS filtradas por estado de envío. Eso es exactamente lo que
   *    era la vieja «Operaciones › Entregas»: los mismos pedidos de Ventas con otro filtro, y por
   *    eso se sentía duplicada. Lo que va acá lista envíos: guía, courier, días en la calle.
   *    Las colas de pedidos viven en `src/lib/operations/queues.ts` y su test impide el solape.
   */
  /**
   * La torre de control de Smarty HQ (27-oct-2026). Red y Reportes entran cuando existan: una entrada
   * que lleva a una pantalla vacía es ruido.
   */
  torre: [
    { url: ROUTES.TORRE, label: 'En vivo' },
    { url: ROUTES.TORRE_PEDIDOS, label: 'Pedidos' },
    { url: ROUTES.TORRE_POR_RESOLVER, label: 'Por resolver' },
  ] satisfies NavLinkItem[],
  entregas: [
    // 🪤 «Por despachar» apunta a la pantalla de logística de siempre, y eso es a propósito. Es la
    //    cola de ENTRADA del módulo: pedidos confirmados que todavía no tienen guía. Ahí vive la
    //    acción masiva de registro en el courier, así que mandarla al descarte antes de tener su
    //    reemplazo habría dejado al equipo sin poder despachar. Lo que cambió es dónde vive: es
    //    trabajo de Entregas, no una pestaña más de Ventas.
    //    Su reemplazo (pedidos sin envío, sobre `fulfillment_status`) llega cuando F1–F3 estén
    //    aplicadas; recién entonces esta entrada apunta a la pantalla nueva.
    { url: ROUTES.OPS_LOGISTICA, label: 'Por despachar' },
    /*
     * ⭐ LAS GUÍAS DE DESPACHO (20261021004300): el trabajo del almacén (sólo quien es almacén), lo que recoge el
     *    courier (sólo quien es courier) y lo que espera, para Smarty. La barra filtra cada una por papel.
     *    «Por despachar» se queda: ahí vive el registro masivo en Shalom, hasta que pase a la guía de Shalom.
     */
    { url: ROUTES.ENTREGAS_GUIAS, label: 'Guías de despacho' },
    { url: ROUTES.ENTREGAS_POR_RECOGER, label: 'Por recoger' },
    /* 🪤 Las liquidaciones del courier ya NO viven acá (27-oct-2026): la plata va en su sección, «Liquidaciones», al lado
     *    de la Billetera (como Stripe y Shopify: Envíos y Pagos son menús distintos). La dirección vieja redirige. */
    { url: ROUTES.ENTREGAS, label: 'Envíos' },
    { url: ROUTES.ENTREGAS_EN_ESPERA, label: 'En espera' },
    /* ⭐ Las novedades de entrega (20261028140000): la visita fallida espera la decisión de la tienda. La ven todos los
     *    papeles (la tienda decide, el courier ve lo reagendado, el almacén recibe lo que vuelve, Smarty ve todo): la
     *    pantalla muestra a cada uno lo suyo, y la barra no la filtra. */
    { url: ROUTES.ENTREGAS_NOVEDADES, label: 'Novedades' },
    // 🪤 El rendimiento del RECAUDO presupone que cobrás contra entrega con gente tuya. El
    //    revendedor de Smarty Drop paga por adelantado y despacha por agencia: no hay recaudo que
    //    rendir. Se le prende el día que tenga flota.
    { url: ROUTES.OPS_ENTREGAS_RECAUDO, label: 'Rendimiento', feature: 'flota_propia' as FeatureName },
  ] satisfies NavLinkItem[],
  /**
   * Transporte: agencias y motorizados.
   *
   * 🪤 ESTUVO ABSORBIDO DENTRO DE ENTREGAS Y VOLVIÓ A SALIR, por decisión del founder. El §5 del
   *    ADR argumentaba que el courier es CON QUIÉN se entrega y no un módulo aparte — y es cierto
   *    del lado del modelo. Pero la palabra importa: en varios países de la región a esto se le
   *    dice «transportadora», y esconderlo adentro de Entregas obliga a saber dónde buscar. La
   *    coherencia del modelo no se paga con la orientación del usuario.
   */
  transporte: [
    { url: ROUTES.TRANSPORT_AGENCIES, label: 'Agencias' },
    // 🪤 Motorizados es flota propia. El revendedor despacha por agencia — y el founder lo
    //    confirmó: «no se usa». Agencias se queda: es donde conecta Shalom.
    { url: ROUTES.TRANSPORT_MOTORIZADOS, label: 'Motorizados', feature: 'flota_propia' as FeatureName },
    /*
     * 🪤 RÓTULOS VA AL FINAL, y el founder lo pidió así. Los dos de arriba son CON QUIÉN se
     *    despacha —lo que se toca a diario—; éste es cómo sale el papel, que se configura una vez y
     *    casi no se vuelve a abrir. Lo frecuente arriba.
     */
    { url: ROUTES.TRANSPORT_ROTULOS, label: 'Rótulos' },
  ] satisfies NavLinkItem[],
  // 🪤 ACÁ VIVÍA `chatbot`, y era código muerto: ningún ítem del sidebar lo referenciaba, pero
  //    seguía declarado y apuntando al mismo destino que «Flujos (chatbots)» en Agentes IA. Un
  //    menú muerto no molesta hasta que alguien lo revive sin saber que la ruta ya estaba en otro
  //    lado. `navigation.test.ts` ahora no lo permite. La ruta sigue viva y alcanzable; lo que se
  //    borró es la entrada duplicada.
  /** Agent Platform (Smarty Agent Studio — F2/Fase B).
   *  Los Chatbots viven aquí como "Flujos": son agentes deterministas sin LLM,
   *  caso particular de la misma plataforma (ver [[project_smarty_agent_platform_vision]]).
   */
  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   * 🚨 ERAN DIEZ ENTRADAS PARA CONFIGURAR UN AGENTE, ENCIMA DE LAS OCHO SECCIONES DEL ESTUDIO
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   * Cerca de veinte destinos para una sola cosa, y tres preguntas que se respondían en más de un
   * lugar: «¿cómo le va?» en Outcomes y en el Estudio; «¿funciona bien?» en Conversaciones de
   * prueba y en Calidad; «¿qué puede hacer?» en Herramientas y en Acciones.
   *
   * 🪤 QUE SEA EL MISMO COMPONENTE MONTADO DOS VECES NO LO SALVA: el usuario no sabe que es el
   * mismo. Ve dos entradas con nombres distintos y tiene que descubrir, entrando, que son lo
   * mismo. Y la duda de «¿en cuál estaba?» no se paga una vez: se paga en cada visita.
   *
   * 🪤 LAS RUTAS SIGUEN VIVAS. Sacar del menú no es borrar: los enlaces guardados funcionan y el
   * contenido está en el Estudio, que es donde el dueño ya lo busca.
   */
  agents: [
    { url: ROUTES.AGENTS_STUDIO, label: 'Mis agentes' },
    /*
     * 🪤 «CONSTRUCTOR» Y «EMBUDO» ERAN EL MISMO COMPONENTE EN DOS RUTAS —los dos montan
     * `AgentConstructorPage`—, con dos nombres distintos en el menú. Queda uno, y se llama por lo
     * que el dueño dibuja ahí.
     */
    { url: ROUTES.AGENTS_CONSTRUCTOR, label: 'Flujos del agente' },
    // Qué sabe y qué aprendió (las preguntas mineradas que esperan el OK) viven acá desde el 13-sep.
    { url: ROUTES.AGENTS_KNOWLEDGE_BASE, label: 'Base de conocimiento' },
    // Los chatbots clásicos (`/apps/chatbot`, sin IA) salieron de este módulo el 13-sep: 0 corridas
    // en WhatsApp en toda la historia; sólo los usa el widget web. Viven en Apps.
    { url: ROUTES.AGENTS_CATALOG, label: 'Catálogo' },
    // Salió del Estudio: es del negocio, no de un agente en particular.
    { url: ROUTES.AGENTS_ESTRATEGIA, label: 'Estrategia comercial' },
    { url: ROUTES.AGENTS_CONFIRMADOR, label: 'Confirmador de pedidos' },
    // Estilo de la IA vive adentro del agente (sección «Estilo») desde el 12-sep-2026.
    // Pulgares abajo salió el 13-sep: 0 votos en la historia; la cola de calidad es El agente › Calidad.
    /*
     * ── Fuera del menú, dentro del Estudio ────────────────────────────────────────────────────
     * `AGENTS_PRUEBAS`    → Estudio › Calidad
     * `AGENTS_HERRAMIENTAS` → Estudio › Acciones
     * `AGENTS_GHOSTWRITER`  → se usa al CREAR un agente, no al configurarlo: su lugar es un botón
     *                         en «Mis agentes», no un renglón permanente del menú.
     * `/agents/outcomes` y `/agents/embudo` ya no existen: redirigen (RUTAS_MUDADAS).
     */
  ] satisfies NavLinkItem[],
  settings: [
    { url: ROUTES.SETTINGS_GENERAL, label: 'General' },
    { url: ROUTES.SETTINGS_BUSINESS, label: 'Datos de la empresa' },
    { url: ROUTES.SETTINGS_PLAN, label: 'Plan y facturación' },
    { url: ROUTES.SETTINGS_USERS, label: 'Usuarios y permisos' },
    { url: ROUTES.SETTINGS_PAYMENTS, label: 'Pagos', feature: 'ecommerce' as FeatureName },
    { url: ROUTES.SETTINGS_CHECKOUT, label: 'Checkout', feature: 'ecommerce' as FeatureName },
    { url: ROUTES.SETTINGS_SHIPPING, label: 'Envíos y entrega', feature: 'ecommerce' as FeatureName },
    { url: ROUTES.SETTINGS_DOMAINS, label: 'Dominios', feature: 'ecommerce' as FeatureName },
    { url: ROUTES.SETTINGS_BRAND, label: 'Marca', feature: 'ecommerce' as FeatureName },
    { url: ROUTES.SETTINGS_NOTIFICATIONS, label: 'Notificaciones', feature: 'ecommerce' as FeatureName },
    { url: ROUTES.SETTINGS_APPS_AND_SALES_CHANNELS, label: 'Apps y canales' },
    { url: ROUTES.SETTINGS_INTEGRATIONS, label: 'Integraciones' },
  ] satisfies NavLinkItem[],
} as const;

/**
 * Sub-ítems del Settings sidebar (fuente de verdad para los desplegables de
 * Usuarios y Envíos que consume SettingsNav.tsx). El resto del menú de Settings
 * vive en el array GROUPS de SettingsNav.tsx.
 */
export const SETTINGS_NAV = {
  usersSubItems: [
    { url: ROUTES.SETTINGS_USERS_ROLES, label: 'Roles' },
    { url: ROUTES.SETTINGS_USERS_SECURITY, label: 'Seguridad' },
  ] satisfies NavLinkItem[],
  shippingSubItems: [
    { url: ROUTES.SETTINGS_DELIVERY_METHODS, label: 'Métodos de entrega' },
    { url: ROUTES.SETTINGS_DELIVERY_RULES, label: 'Reglas de delivery' },
    { url: ROUTES.SETTINGS_LOGISTICS_COURIER_RULES, label: 'Couriers por zona' },
    { url: ROUTES.SETTINGS_SHIPPING_METHODS, label: 'Métodos de envío' },
  ] satisfies NavLinkItem[],
} as const;

export const NAV_BADGES = {
  soon: 'Próx.',
  beta: 'Beta',
} as const;

type BadgeKey = keyof typeof NAV_BADGES;

type SidebarItemKey =
  | 'home'
  | 'novedades'
  | 'torre'
  | 'orders'
  | 'products'
  | 'customers'
  | 'content'
  | 'analytics'
  | 'marketing'
  | 'discounts'
  | 'onlineStore'
  | 'pos'
  | 'marketplaces'
  | 'whatsapp'
  | 'loyalty'
  | 'cod'
  | 'crm'
  | 'callCenter'
  | 'avisos'
  | 'operaciones'
  | 'entregas'
  | 'transporte'
  | 'dropshipping'
  // Ítems propios del grupo «Dropshipping» (22-sep-2026): la billetera y el mostrador dejaron de
  // ser pestañas del catálogo. Ver `SIDEBAR_MAIN`.
  | 'billetera'
  | 'almacenDrop'
  | 'inventarioDrop'
  | 'liquidacionDrop'
  | 'couriersDrop'
  | 'chatbot'
  | 'ads'
  | 'agentsStudio'
  | 'contabilidad'
  | 'modules'
  | 'settings';

// Note: Not all SidebarItemKey values are used in the current SIDEBAR_MAIN.
// Some items (callCenter, transporte, chatbot, content, marketing, discounts, modules)
// were consolidated into parent sections or moved to sub-navigation for a cleaner sidebar.
// The keys remain in the union type for backward compatibility with Sidebar.tsx.

type SidebarSectionKey = 'core' | 'dropshippingGroup' | 'salesChannels' | 'apps' | 'footer';

/**
 * Por qué una página no se muestra todavía.
 *
 * 🚨 RETIRADA NO ES LO MISMO QUE APAGADA. `feature` responde «este cliente no contrató el módulo»;
 * esto responde «esto todavía no está construido, no tiene caso mostrárselo a NADIE». Un módulo
 * apagado tiene una pantalla que funciona del otro lado; una página retirada no.
 *
 * ⭐ Y ES EL ÚNICO LUGAR DONDE SE DECLARA. El sidebar la esconde leyendo esto, y la lista del Super
 * Admin se arma leyendo esto mismo — no es una lista escrita a mano al lado. Retirar una página es
 * agregar este campo y nada más; olvidarse de anotarla en la lista es imposible porque la lista ES
 * el sidebar mirado del otro lado.
 */
export type PaginaRetirada = {
  /** Qué le falta, en las palabras que uno usaría al retomarla. */
  motivo: string;
  /** Desde cuándo está fuera (AAAA-MM-DD): sirve para ver qué se está durmiendo hace meses. */
  desde: string;
};

export type SidebarNavItem = {
  key: SidebarItemKey;
  url: string;
  label: string;
  subnavKey?: keyof typeof SIDEBAR_SUBNAV;
  badge?: BadgeKey;
  feature?: FeatureName;
  /** Presente = fuera del sidebar para todos. La ruta sigue viva; sólo deja de ofrecerse. */
  retirada?: PaginaRetirada;
};

/**
 * Sidebar — restauración de descubribilidad total.
 *
 * Filosofía: cada módulo es un canal/app independiente con su propia config.
 * Visibilidad máxima de opciones; el usuario decide su orden.
 * (Versión previa había aplicado regla de Miller 7±2 ocultando 9 items L1
 *  y 17 subitems. Restaurado por solicitud del founder.)
 */
export const SIDEBAR_MAIN = {
  sections: [
    {
      key: 'core' as const,
      title: undefined,
      items: [
        { key: 'home', url: ROUTES.HOME, label: 'Inicio', subnavKey: 'home' },
        /*
         * ⭐ NOVEDADES, AL LADO DEL INICIO. Para el drop es lo que Smarty publicó para él; para Smarty
         *    HQ, la barra lo lleva a publicar (ver `aDondeLlevanLasNovedades` en la barra). A una tienda
         *    que no participa de Drop no le llega nada, así que no la ve.
         */
        { key: 'novedades', url: ROUTES.NOVEDADES, label: 'Novedades' },
        /*
         * ⭐ LA TORRE DE CONTROL (27-oct-2026): la red entera, en vivo, para Smarty HQ. Sólo la plataforma: la barra
         *    lo filtra, y la base lo exige igual (fn_drop_soy_la_plataforma).
         */
        { key: 'torre', url: ROUTES.TORRE, label: 'Torre de control', subnavKey: 'torre', feature: 'logistics' as FeatureName },
        /*
         * 🪤 PEDIDOS ES LO QUE ENTRA POR LA WEB, y sin storefront no entra nada. Estaba sin gate
         *    porque hasta el plan Drop todos tenían `ecommerce`: el gate no faltaba, sobraba la
         *    coincidencia. Al revendedor le mostraba dos pantallas siempre vacías — y peor:
         *    «Pedidos» al lado de «Ventas» y «Mis compras» son tres entradas que suenan igual.
         *
         *    El founder: «se puede jalar cuando integre Shopify, por ahora oculta». Vuelve sola el
         *    día que el plan tenga con qué llenarla.
         */
        /*
         * 🪤 Y EL 1-SEP-2026 DEJÓ DE COLGAR DE `ecommerce`. El motivo de arriba sigue siendo válido
         *    para el revendedor —no tiene canal web y le quedaban dos pantallas vacías— pero la
         *    premisa «sin tienda propia no hay pedidos web» se cayó: con Shopify conectado entran
         *    pedidos y carritos abandonados sin que exista tienda de Smarty. La entrada ahora pide
         *    `pedidos_online`, que es tener UN canal web, no tener el nuestro.
         */
        { key: 'orders', url: ROUTES.ORDERS_ONLINE, label: 'Pedidos', subnavKey: 'orders', feature: 'pedidos_online' as FeatureName },
        { key: 'products', url: ROUTES.PRODUCTS, label: 'Productos', subnavKey: 'products' },
        { key: 'customers', url: ROUTES.CUSTOMERS, label: 'Clientes', subnavKey: 'customers' },
        { key: 'crm', url: ROUTES.APPS_CRM_INBOX, label: 'CRM', subnavKey: 'crm', feature: 'crm' as FeatureName },
        /*
         * 🚨 «AUTOMATIZACIÓN» ES DE PRIMER NIVEL, NO UNA APP (12-sep-2026). Estaba bajo «Apps» como
         * «Agentes IA», al lado de Contabilidad y Fidelización — como si el agente que vende fuera
         * opcional. Para un negocio COD es el producto. Y lo que hacen las grandes es un solo lugar
         * para construir: Zendesk (Admin Center), HubSpot (Automation), ManyChat (Automation),
         * uChat (el bot y sus flujos). Adentro viven el agente, los flujos, los disparadores, las
         * plantillas y las campañas. Ver docs/plan/VEREDICTO-FLUJOS-Y-DISPARADORES-2026-09-12.md §8b.
         */
        { key: 'agentsStudio', url: ROUTES.AGENTS_STUDIO, label: 'Automatización', subnavKey: 'agents', feature: 'agents' as FeatureName },
        /*
         * 🚨 ACÁ DECÍA «CONTENIDO ES LA TIENDA: páginas y blog del storefront», Y NO ES ESO.
         *    `ContentPage` es la BIBLIOTECA DE MEDIOS —su propio encabezado lo dice, «Página de
         *    Contenido (medios/imágenes)»— y trabaja con `listMediaAssets` / `uploadAsset` /
         *    `deleteAsset`. Las páginas y el blog del storefront viven en `ONLINE_STORE_PAGES`.
         *
         *    El gate se puso el 25-ago sobre esa identificación equivocada, y la consecuencia la
         *    encontró el founder mirando el panel de un cliente: `ecommerce` está en desarrollo, así
         *    que la biblioteca donde se ven TODAS las imágenes desaparecía del menú de una tienda
         *    que sí las necesita para sus productos.
         *
         * ⭐ El gate correcto es `catalogo_propio`: subir y administrar TUS imágenes es parte de
         *    administrar TU catálogo. Con eso el plan Drop sigue sin verlo —que era la intención
         *    original y se conserva— y Básico, Pro y Enterprise lo recuperan.
         */
        { key: 'content', url: ROUTES.CONTENT, label: 'Contenido', feature: 'catalogo_propio' as FeatureName },
        /*
         * 🪤 «QUITÁ ANALÍTICAS POR AHORA PORQUE NO ESTÁ BIEN DESARROLLADO. CUANDO ESTÉ LISTO SE
         *    VUELVE A LANZAR» — y por eso va como `feature` y no como `retirada`: no está rota
         *    para todos, está sin terminar PARA EL PLAN DROP. Prenderla después es un UPDATE en
         *    `plans.features`, sin deploy.
         */
        { key: 'analytics', url: ROUTES.ANALYTICS, label: 'Analíticas', subnavKey: 'reports', feature: 'analiticas' as FeatureName },
        {
          key: 'marketing', url: ROUTES.MARKETING, label: 'Marketing', feature: 'marketing' as FeatureName,
          retirada: { motivo: 'Pendiente por construir: la pantalla existe pero no hace campañas todavía.', desde: '2026-08-18' },
        },
        {
          key: 'discounts', url: ROUTES.DISCOUNTS, label: 'Descuentos', feature: 'discounts' as FeatureName,
          retirada: { motivo: 'Pendiente por construir: los descuentos del pedido se aplican a mano desde la venta.', desde: '2026-08-18' },
        },
        // «Operaciones» era un contenedor abstracto, no un objeto de negocio: nadie dice «voy a
        // operaciones», dice «voy a ver las ventas» o «voy a despachar». Ahora son dos módulos con
        // su objeto: Ventas (el pedido) y Entregas (el envío).
        { key: 'operaciones', url: ROUTES.OPS_VENTAS, label: 'Ventas', subnavKey: 'operaciones', feature: 'operaciones' as FeatureName },
        // Entregas (ADR 0029). Convive con «Operaciones › Entregas» y con «Transporte» durante la
        // migración: son el estado intermedio del expand→contract. Al retirarse el modelo viejo
        // (F6), esta absorbe Transporte y la pestaña de logística desaparece de Operaciones.
        /*
         * ⭐ AVISOS SALE DE AJUSTES Y SE PONE EN EL MENÚ. La pantalla existía —mapea cada evento del
         *    negocio a un grupo de WhatsApp— pero vivía enterrada como «Notificaciones por
         *    WhatsApp». Medido: de nueve comercios, UNO tenía alguna regla. Y no era falta de
         *    acceso: Dulti tiene seis líneas de WhatsApp conectadas y cero reglas configuradas.
         *
         *    Un nombre que suena a ajuste de WhatsApp esconde lo que en realidad hace: avisarte
         *    cuando vendés.
         */
        { key: 'avisos', url: ROUTES.AVISOS, label: 'Avisos', feature: 'avisos' as FeatureName },
        { key: 'entregas', url: ROUTES.ENTREGAS, label: 'Entregas', subnavKey: 'entregas', feature: 'logistics' as FeatureName },
        { key: 'transporte', url: ROUTES.TRANSPORT_AGENCIES, label: 'Transporte', subnavKey: 'transporte', feature: 'logistics' as FeatureName },
      ] satisfies SidebarNavItem[],
    },
    /*
     * ⭐ DROPSHIPPING ES UN GRUPO, NO UN MÓDULO (22-sep-2026). Era UN ítem con cinco hijos, y el
     *    founder pidió partirlo: «hagamos esa etiqueta de dropshipping y ahí coloquemos catálogo y
     *    debajo billetera, y cada opción sea una opción padre, porque dentro de Drop confundiré al
     *    dropshipper».
     *
     *    Es la misma forma que ya tiene «Canales»: un título de grupo y adentro cosas que no son
     *    una sola. Y son tres objetos distintos —dónde compro, mi plata, mi mostrador—, no tres
     *    pestañas de lo mismo.
     */
    {
      key: 'dropshippingGroup' as const,
      title: 'Dropshipping',
      items: [
        /*
         * 🪤 SE LLAMA «CATÁLOGO» Y NO «SMARTY DROP». El founder lo dudó en voz alta —«no sé si sea
         *    bueno colocar Smarty Drop sabiendo que es el único catálogo»— y la duda tenía razón:
         *    el grupo YA dice «Dropshipping», así que repetir la marca adentro de nuestra propia
         *    casa no agrega nada. Y el día que el cliente sume Dropi o Aliclik, «Catálogo» sigue
         *    siendo el nombre correcto: las plataformas son los hijos, por su marca.
         *
         *    La `key` sigue siendo `dropshipping` a propósito: es la que el rol Drop nombra en su
         *    lista blanca y la que traduce y redirige.
         */
        /*
         * 🪤 APUNTA AL CATÁLOGO, NO AL HUB. `/dropshipping` es un redirector, y mandar el menú ahí
         *    agregaba un salto —el portón resolviendo su estado antes de decidir— que se veía como
         *    un parpadeo de recarga. El destino directo no parpadea.
         */
        { key: 'dropshipping', url: ROUTES.DROPSHIPPING_SMARTYDROP, label: 'Catálogo', subnavKey: 'dropshipping', feature: 'logistics' as FeatureName },
        /*
         * ⭐ UNA BILLETERA, DOS LECTURAS, Y LAS DOS SON «TU PLATA». Para el revendedor es lo que le
         *    queda para comprar; para Smarty HQ es lo que RECAUDÓ y lo que falta aprobar. El
         *    destino lo resuelve la barra lateral con `esLaPlataforma`, porque es la misma pregunta
         *    con distinto dueño — no dos entradas distintas compitiendo por el mismo nombre.
         */
        { key: 'billetera', url: ROUTES.DROPSHIPPING_SMARTYDROP_SALDO, label: 'Billetera', feature: 'logistics' as FeatureName },
        /*
         * ⭐ LIQUIDACIONES, AL LADO DE LA BILLETERA (27-oct-2026). Una sola entrada para todos los actores —tienda,
         *    proveedor, almacén, courier; Smarty ve la de los couriers— con la estructura por día que el founder eligió
         *    («me gusta como se ve así con el desplegable por días»). Reemplaza a «Liquidación», «Liquidación por día»,
         *    «Liquidaciones de couriers» y Entregas → Liquidaciones, que redirigen a su pestaña.
         */
        { key: 'liquidacionDrop', url: ROUTES.DROPSHIPPING_SMARTYDROP_LIQUIDACION, label: 'Liquidaciones', feature: 'logistics' as FeatureName },
        /*
         * 🪤 SÓLO PARA QUIEN PUBLICA CATÁLOGO. La barra lo filtra: un tenant que no es proveedor no
         *    tiene nada que configurar acá, y una entrada que lleva a una pantalla vacía es ruido
         *    en el menú de TODAS las tiendas.
         */
        { key: 'almacenDrop', url: ROUTES.DROPSHIPPING_SMARTYDROP_ALMACEN, label: 'Mi almacén', feature: 'logistics' as FeatureName },
        /* Sólo para quien tiene stock que mirar: proveedor o almacén. Lo decide la barra por papeles. */
        { key: 'inventarioDrop', url: ROUTES.DROPSHIPPING_SMARTYDROP_INVENTARIO, label: 'Inventario', feature: 'logistics' as FeatureName, subnavKey: 'inventarioDrop' },
        /*
         * El courier de Lima: su perfil, sus motorizados y sus tarifas. Sólo lo ve el courier: trabaja
         * con Smarty Drop, y los demás lo eligen en Transporte (20260925190000).
         */
        { key: 'couriersDrop', url: ROUTES.DROPSHIPPING_SMARTYDROP_COURIERS, label: 'Tu courier', feature: 'logistics' as FeatureName },
      ] satisfies SidebarNavItem[],
    },
    {
      key: 'salesChannels' as const,
      title: 'Canales',
      items: [
        { key: 'whatsapp', url: ROUTES.WHATSAPP_INBOX, label: 'Chats', subnavKey: 'whatsapp', feature: 'whatsapp' as FeatureName },
        // 🪤 Gateaba con `crm`, o sea que el pipeline y el call center eran el mismo interruptor.
        // Ahora tiene el suyo (`callCenter`, en desarrollo): el cliente ve el CRM sin las llamadas.
        { key: 'callCenter', url: ROUTES.APPS_CRM_DIALER, label: 'Llamadas', subnavKey: 'callCenter', feature: 'call_center' as FeatureName },
        { key: 'onlineStore', url: ROUTES.ONLINE_STORE, label: 'Tienda online', subnavKey: 'onlineStore', feature: 'ecommerce' as FeatureName },
        { key: 'pos', url: ROUTES.APPS_RESTAURANT_POS, label: 'Punto de venta', subnavKey: 'pos', feature: 'pos' as FeatureName },
        /*
         * 🪤 MARKETPLACES ES DEL VENDEDOR, NO DEL REVENDEDOR. Conectar Mercado Libre o Falabella
         * es publicar TU catálogo en un canal ajeno: presupone tienda y stock propio. Un revendedor
         * de Smarty Drop no tiene ni una cosa ni la otra — le estaríamos ofreciendo enchufar un
         * catálogo que no es suyo.
         *
         * Igual que `content`, estaba sin gatear porque todos los planes tenían `ecommerce` y la
         * distinción nunca se notó.
         */
        { key: 'marketplaces', url: ROUTES.MARKETPLACES, label: 'Marketplaces', subnavKey: 'marketplaces', feature: 'marketplaces' as FeatureName },
      ] satisfies SidebarNavItem[],
    },
    {
      key: 'apps' as const,
      title: 'Apps',
      items: [
        { key: 'ads', url: ROUTES.ADS_CHAT, label: 'Smarty Ads', badge: 'beta', feature: 'ads' as FeatureName },
        { key: 'contabilidad', url: ROUTES.APPS_CONTABILIDAD, label: 'Contabilidad', subnavKey: 'contabilidad', badge: 'beta', feature: 'accounting' as FeatureName },
        { key: 'loyalty', url: ROUTES.APPS_LOYALTY, label: 'Sellos', subnavKey: 'loyalty', feature: 'loyalty' as FeatureName },
        { key: 'cod', url: ROUTES.APPS_COD, label: 'Formulario COD', subnavKey: 'cod', feature: 'cod' as FeatureName },
      ] satisfies SidebarNavItem[],
    },
    {
      key: 'footer' as const,
      title: undefined,
      items: [
        { key: 'settings', url: ROUTES.SETTINGS, label: 'Configuración', subnavKey: 'settings' },
      ] satisfies SidebarNavItem[],
    },
  ] satisfies Array<{ key: SidebarSectionKey; title?: string; items: SidebarNavItem[] }>,
} as const;


/**
 * Las páginas que hoy NO se ofrecen, leídas del MISMO sidebar que las esconde.
 *
 * 🚨 POR QUÉ SE DERIVA Y NO SE ESCRIBE. El founder lo pidió así: «colocá las páginas retiradas
 * momentáneamente o pendientes por construir, para no olvidarme». Una lista escrita a mano al lado
 * del sidebar se olvida exactamente cuando importa —al retirar la número once, con apuro— y a los
 * dos meses nadie sabe si está completa. Derivada, la lista no puede quedar corta: si la página
 * desapareció del menú, es porque tiene `retirada`, y si tiene `retirada` está acá.
 *
 * Se recorre en el orden del sidebar, que es el orden en que el founder las tiene en la cabeza.
 */
export const PAGINAS_RETIRADAS: Array<{
  key: string;
  label: string;
  url: string;
  seccion: SidebarSectionKey;
  /** Si es un sub-item, de qué ítem del menú colgaba (ej. «CRM»). */
  dentroDe?: string;
  retirada: PaginaRetirada;
}> = SIDEBAR_MAIN.sections.flatMap((seccion) =>
  seccion.items.flatMap((item) => {
    const propia = item.retirada
      ? [{ key: item.key as string, label: item.label, url: item.url, seccion: seccion.key, retirada: item.retirada }]
      : [];

    /*
     * 🪤 TAMBIÉN LOS SUB-ITEMS. Retirar «Marketing» entero es el caso fácil; lo que más va a pasar
     * es sacar UNA pantalla de adentro de un módulo que sí funciona. Si la lista sólo mirara el
     * primer nivel, esas desaparecerían del menú sin dejar rastro — el olvido que esto evita, por
     * la puerta de al lado.
     */
    const subs = item.subnavKey
      ? SIDEBAR_SUBNAV[item.subnavKey]
          .filter((sub): sub is typeof sub & { retirada: PaginaRetirada } => sub.retirada != null)
          .map((sub) => ({
            key: sub.url, label: sub.label, url: sub.url,
            seccion: seccion.key, dentroDe: item.label, retirada: sub.retirada,
          }))
      : [];

    return [...propia, ...subs];
  })
);

/**
 * ── EL MENÚ DEL ROL DROPSHIPPER ─────────────────────────────────────────────────────────────────
 *
 * 🚨 LO QUE DIJERON LOS DROPSHIPPERS (19-sep-2026): «no sabemos dónde hacer la venta, dónde
 *    recargar, dónde seleccionar la entrega». Y lo que veían: 8 módulos y ~25 destinos —incluidos
 *    Entregas y Transporte, que son para tiendas que despachan solas— para alguien cuyo trabajo es
 *    una sola secuencia. Medido: todos se quedan en una o dos ventas y no vuelven.
 *
 * ⭐ EL MENÚ ES DEL ROL, NO DEL PRODUCTO. La app del repartidor no muestra contabilidad; Amazon
 *    Seller le muestra al vendedor cuatro cosas. El dropshipper necesita: Inicio · Ventas · Catálogo
 *    · Mis compras · Billetera · Clientes · Configuración. Y nada más.
 *
 * 🪤 ES UNA LISTA BLANCA, NO UNA NEGRA. Con una lista negra, el próximo módulo que alguien agregue
 *    aparece solo en el menú del dropshipper y nadie se entera hasta la próxima ronda de quejas.
 *    Con lista blanca, lo nuevo NO entra hasta que alguien decida que le sirve.
 *
 * 🪤 Y «DROPSHIPPING → SMARTY DROP» PASA A LLAMARSE «CATÁLOGO». Que el módulo se llame igual que el
 *    producto entero era una de las confusiones que nombraron.
 */
export const MENU_DEL_ROL_DROP = {
  /**
   * Ítems del menú principal que ve el plan Drop, por `key`, en este orden.
   *
   * 🚨 «MIS PRODUCTOS» SE FUE DEL MENÚ, Y ESTO REVIERTE UNA DECISIÓN DE HACE TRES DÍAS.
   *    El 21-sep el founder lo sacó de dropshipping y lo devolvió a su módulo: «mis productos no
   *    debe estar en dropshipping, debe estar aparte como estaba antes». El 23-sep de noche, ya
   *    usándolo, dijo lo contrario y con un motivo mejor: «la opción de Productos en el Drop no es
   *    relevante, ya no lo usa; quizás se puede añadir otro tab de importados».
   *
   *    Y los números le dan la razón: en los nueve negocios del plan Drop hay 21 productos y los 21
   *    son importados (medido el 24-sep). El módulo y el catálogo mostraban la misma lista en dos
   *    lugares del menú. Ahora es la pestaña «Míos», al lado de «Productos» y «Favoritos»: lo que
   *    PUEDO traer · lo que estoy MIRANDO · lo que ya TRAJE, un viaje en una pantalla.
   *
   * 🪤 LA FICHA DEL PRODUCTO NO SE FUE: sigue en la lista blanca de rutas y la pestaña enlaza a
   *    ella (ProductEditPage: fotos, precio, nombre, descripción, categoría — nada de existencias,
   *    variantes ni canales). Lo que se fue es la LISTA, que estaba duplicada.
   */
  /*
   * ⭐ «TRANSPORTE» EN VEZ DE «COURIERS» (25-sep-2026). El founder: «en vez de courier coloca el módulo de
   *    transporte que ya tenemos; dentro de transporte los drops deben poder ver los couriers motorizados
   *    y las agencias tal cual», como en los planes de pago. Sus couriers de Lima se invitan arriba de
   *    Motorizados.
   */
  items: ['home', 'novedades', 'operaciones', 'dropshipping', 'billetera', 'liquidacionDrop', 'customers', 'transporte', 'settings'] as const,
  /**
   * Cómo se llama cada módulo para este rol; los que no están acá conservan su nombre.
   *
   * ⭐ EL CRITERIO DE NOMBRES (21-sep-2026): el MÓDULO se llama como la actividad y las ENTRADAS
   *    como las plataformas, por su marca. En los planes de pago el módulo es «Dropshipping» y
   *    adentro van Smarty Drop · Dropi · Aliclik (un cliente puede trabajar con las tres). Para el
   *    rol Drop, que sólo tiene la nuestra, el módulo se llama «Drop» —como su plan y como el paso
   *    «Drop» de la venta: una palabra para una cosa— y adentro «Smarty Drop» es el catálogo, con
   *    el mismo nombre que verá si algún día pasa a un plan de pago.
   */
  etiquetas: {
    /*
     * ⚠️ DECÍA «DROP», Y CAMBIÓ EL 22-sep-2026. El criterio de arriba sigue en pie —el módulo se
     *    llama como la actividad— pero ya no hay módulo: «Dropshipping» pasó a ser el TÍTULO DEL
     *    GRUPO, y adentro cada cosa es un ítem propio. Entonces esta entrada dejó de ser «el
     *    módulo» y pasó a ser lo que siempre fue: el catálogo donde compra.
     *
     *    El founder: «dentro de Drop confundiré al dropshipper». Un ítem llamado «Drop» dentro de
     *    un grupo llamado «Dropshipping» es exactamente esa confusión.
     */
    dropshipping: 'Catálogo',
    /*
     * ⭐ «MIS PRODUCTOS», NO «PRODUCTOS». Al lado de «Catálogo» la palabra sola no dice cuál es
     *    cuál, y son dos objetos distintos: el catálogo es lo que PUEDO traer —no es mío, y el
     *    precio que muestra es el costo mayorista—; esto es lo que YA traje y estoy vendiendo, con
     *    MI precio de venta. Sin esta pantalla el revendedor no tiene dónde ponerlo.
     *
     *    Es además la palabra que usó el founder cuando lo sacó de Dropshipping: «mis productos no
     *    debe estar en dropshipping, debe estar aparte como estaba antes».
     */
    products: 'Mis productos',
  } as Record<string, string>,
  /**
   * 🪤 A DÓNDE LLEVA EL MÓDULO PARA ESTE ROL. «Dropshipping» en general abre el hub (Dropi primero);
   *    el dropshipper sólo tiene el catálogo de Smarty Drop — el founder vio «Catálogo Dropi» al
   *    entrar y pidió retirarlo del plan. Las rutas de Dropi/Aliclik además lo redirigen (AppRoutes).
   */
  urls: {
    dropshipping: ROUTES.DROPSHIPPING_SMARTYDROP,
  } as Record<string, string>,
  /**
   * El subnav COMPLETO de cada módulo para este rol. Se define entero y no como filtro del general,
   * porque «Nueva venta» no existe en el subnav general —ahí se crea desde un botón de la lista— y
   * acá tiene que ser la primera cosa que se ve: es la pregunta que nadie sabía contestar.
   * Un módulo que no está acá muestra su subnav general.
   */
  subnav: {
    /*
     * ⭐ EL INICIO NO TIENE SUBMENÚ (21-sep-2026). Sin esto heredaba el general (Resumen · Primeros
     *    pasos): «Primeros pasos» es la guía de una TIENDA (catálogo, canales, cobros) y el portón del
     *    rol ni lo deja abrir. El founder: «quita la opción de primeros pasos». Y «Resumen» solo, como
     *    único ítem, no organiza nada. Vacío = el módulo es una sola página.
     */
    home: [],
    /*
     * ⭐ VENTAS SE VE COMO EN LOS PLANES DE PAGO: Por confirmar · Ventas · Cobranzas · Canceladas.
     *    El founder (21-sep-2026): «ahí en venta no debemos crear la rueda: el mismo orden y la
     *    misma forma que en los planes de pago, así el drop controla sus ventas y en qué estado
     *    están». «Nueva venta» salió del menú (se ve mal ahí): se crea desde el botón de la lista y
     *    desde el Inicio. Lo único que NO entra es «Drop» (la cola del almacén, sólo proveedores).
     */
    operaciones: [
      { url: ROUTES.OPS_PEDIDOS, label: 'Por confirmar' },
      { url: ROUTES.OPS_VENTAS, label: 'Ventas' },
      { url: ROUTES.OPS_COBRANZAS, label: 'Cobranzas' },
      { url: ROUTES.OPS_CANCELADAS, label: 'Canceladas' },
      // 20-oct-2026: la configuración de Ventas (dar por cobrada al entregar). El vendedor no la ve: no es su ruta.
      { url: ROUTES.OPS_SETTINGS, label: 'Configuración' },
    ],
    // Agencias y Motorizados, como en los planes de pago. Sin «Rótulos»: el rótulo lo imprime el almacén.
    transporte: [
      { url: ROUTES.TRANSPORT_AGENCIES, label: 'Agencias' },
      { url: ROUTES.TRANSPORT_MOTORIZADOS, label: 'Motorizados' },
    ],
    // 🪤 SIN «MIS COMPRAS» (21-sep-2026): el dropshipper paga cada pedido desde la venta, y lo que
    //    debe, lo que está en revisión y el historial viven en la Billetera. Dos pantallas de plata
    //    eran una de más (el founder: «no sé si sea necesaria»). La página queda para el almacén.
    /*
     * ⭐ SIN SUBMENÚ (22-sep-2026). El revendedor tiene UN catálogo, así que un submenú con un solo
     *    hijo que se llama casi igual que el padre no organiza nada: sólo agrega un clic. Y la
     *    Billetera, que era el otro hijo, subió a ítem propio del grupo.
     *
     *    Vacío = el ítem es una sola página, la misma regla que ya usa `home`.
     */
    dropshipping: [],
    /*
     * ⭐ SIN SUBMENÚ (22-sep-2026). Acá estaban «Categorías» y «Etiquetas», y los datos dijeron que
     *    no servían: los ocho revendedores tienen entre 1 y 6 productos cada uno, y TODOS tienen
     *    exactamente UNA categoría. Organizar seis productos en categorías es trabajo sin
     *    beneficio — y el dropshipper ni siquiera tiene tienda online donde esa organización se
     *    vería.
     *
     *    El founder lo preguntó mirando el menú: «hay opciones que no sé de qué servirían, como
     *    categorías o etiquetas, si finalmente es sólo catálogo y gestión de pedidos».
     *
     *    Vacío = el ítem es una sola página, igual que `home` y que el catálogo.
     */
    products: [],
    customers: [
      { url: ROUTES.CUSTOMERS, label: 'Clientes' },
    ],
    settings: [
      { url: ROUTES.SETTINGS_GENERAL, label: 'General' },
      { url: ROUTES.SETTINGS_BUSINESS, label: 'Datos de la empresa' },
      { url: ROUTES.SETTINGS_USERS, label: 'Usuarios y permisos' },
      { url: ROUTES.SETTINGS_INTEGRATIONS, label: 'Integraciones' },
    ],
  } as Record<string, ReadonlyArray<{ url: string; label: string }>>,
};
